import { useState, useEffect } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { serviceName, SERVICES } from "../../lib/constants";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Reply, Sparkles, Loader2, Plus, Trash2, Pencil, ShieldCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const EMPTY = { name: "", email: "", phone: "", service_type: "moving", pickup_address: "", dropoff_address: "", property_size: "", preferred_date: "", details: "" };

export default function QuotesInbox() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isDispatcher = user?.role === "dispatcher";
  const [quotes, setQuotes] = useState([]);
  const [responding, setResponding] = useState(null);
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null); // null | { ...quote } | "new"
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState(new Set());

  const load = () => api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (q) => { setForm({ ...EMPTY, ...q }); setEditing(q); };
  const saveForm = async () => {
    try {
      if (editing === "new") {
        await api.post("/quotes", form);
        toast.success("Quote request added");
      } else {
        await api.patch(`/quotes/${editing.id}`, form);
        toast.success("Quote updated");
      }
      setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Quote deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === quotes.length) setSelected(new Set());
    else setSelected(new Set(quotes.map((q) => q.id)));
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} quote${selected.size > 1 ? "s" : ""}?`)) return;
    try {
      const { data } = await api.post("/quotes/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} quote${data.deleted !== 1 ? "s" : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openRespond = (q) => {
    setResponding(q); setDetail(null);
    setAnalysis(q.ai_analysis || null);
    setPrice(q.ai_analysis?.suggested_price ? String(q.ai_analysis.suggested_price) : q.estimated_total ? String(q.estimated_total) : "");
    setMessage(q.ai_analysis?.message || "");
    api.get(`/quotes/${q.id}`).then((r) => setDetail(r.data)).catch(() => {});
  };

  const aiDraft = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/quotes/${responding.id}/ai-draft`);
      setAnalysis(data);
      setPrice(String(data.suggested_price));
      setMessage(data.message);
      toast.success("AI draft ready — review, tweak, send");
    } catch (e) { toast.error(errMsg(e)); }
    setAiLoading(false);
  };

  const respond = async () => {
    try {
      const { data } = await api.post(`/quotes/${responding.id}/respond`, { quoted_price: Number(price), message });
      if (data?.status === "pending_approval") {
        toast.success(`Sent to owner for approval — above the ${fmtMoney(data.limit || 0)} dispatcher limit`);
      } else {
        toast.success("Quote sent — customer notified");
      }
      setResponding(null); setPrice(""); setMessage(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const approve = async (q) => {
    try {
      await api.post(`/quotes/${q.id}/approve`);
      toast.success(`Approved — ${q.name} notified`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="ops-quotes-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline-cc">Lead Generation</p>
          <h1 className="page-title mt-1.5">Quote Requests</h1>
        </div>
        <Button data-testid="quote-new-button" onClick={openNew}
          className="rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> New Quote
        </Button>
      </div>

      {selected.size > 0 && (
        <div data-testid="quotes-bulk-bar" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-semibold text-red-500">{selected.size} selected</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => setSelected(new Set())} data-testid="quotes-bulk-clear">Clear</Button>
            <Button size="sm" onClick={bulkDelete} data-testid="quotes-bulk-delete"
              className="rounded-full h-8 bg-red-500 hover:bg-red-600 text-white font-semibold">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b bg-secondary/50">
              <th className="px-3 py-3 w-10">
                <Checkbox data-testid="quotes-select-all"
                  checked={quotes.length > 0 && selected.size === quotes.length}
                  onCheckedChange={toggleAll} />
              </th>
              {["Customer", "Service", "Route", "Preferred Date", "Submitted", "Status", "Quote", ""].map((h, i) => (
                <th key={i} className="text-left px-5 py-3 th-cc font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {quotes.map((q) => (
              <tr key={q.id} data-testid={`quote-row-${q.id}`}
                className={`hover:bg-secondary/40 transition-colors duration-150 ${selected.has(q.id) ? "bg-[var(--cc-accent)]/5" : ""}`}>
                <td className="px-3 py-3.5">
                  <Checkbox data-testid={`quote-select-${q.id}`} checked={selected.has(q.id)} onCheckedChange={() => toggleOne(q.id)} />
                </td>
                <td className="px-6 py-4">
                  <p className="font-semibold">{q.name}</p>
                  <p className="text-xs text-muted-foreground">{q.email || "no email"}{q.phone ? ` · ${q.phone}` : ""}</p>
                  {q.source === "internal" && <span className="inline-block mt-1 text-[10px] font-mono-cc font-bold tracking-wider text-purple-500 border border-purple-500/40 rounded px-1.5 py-0.5">INTERNAL</span>}
                  {q.ai_analysis && <span className="inline-block mt-1 ml-1 text-[10px] font-mono-cc font-bold tracking-wider text-[var(--cc-accent)] border border-[var(--cc-accent)]/40 rounded px-1.5 py-0.5" data-testid={`quote-ai-tag-${q.id}`}>AI ${q.ai_analysis.suggested_price}</span>}
                </td>
                <td className="px-6 py-4">{serviceName(q.service_type)}</td>
                <td className="px-6 py-4 text-xs text-muted-foreground max-w-[220px]">
                  {q.pickup_address ? `${q.pickup_address.split(",")[0]}${q.dropoff_address ? " → " + q.dropoff_address.split(",")[0] : ""}` : "—"}
                </td>
                <td className="px-6 py-4 text-muted-foreground text-xs">{fmtDate(q.preferred_date)}</td>
                <td className="px-6 py-4 text-muted-foreground text-xs">{fmtDate(q.created_at)}</td>
                <td className="px-6 py-4"><StatusBadge status={q.status} /></td>
                <td className="px-6 py-4 font-semibold">{q.quoted_price ? fmtMoney(q.quoted_price) : q.proposed_price ? <span className="text-amber-500">{fmtMoney(q.proposed_price)} pending</span> : q.estimated_total ? <span className="text-muted-foreground">{fmtMoney(q.estimated_total)} est.</span> : "—"}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1.5 justify-end">
                    {(q.status === "pending" || q.status === "new") && (
                      <Button data-testid={`quote-respond-button-${q.id}`} size="sm"
                        className="rounded-full text-xs btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold"
                        onClick={() => openRespond(q)}>
                        <Reply className="w-3.5 h-3.5 mr-1" /> Send Quote
                      </Button>
                    )}
                    {q.status === "pending_approval" && isAdmin && (
                      <Button data-testid={`quote-approve-button-${q.id}`} size="sm"
                        className="rounded-full text-xs bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                        onClick={() => approve(q)}>
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve & Send
                      </Button>
                    )}
                    {q.status === "pending_approval" && isDispatcher && (
                      <span className="text-[11px] font-mono-cc text-amber-500 uppercase tracking-wider">Awaiting owner</span>
                    )}
                    <Button data-testid={`quote-edit-${q.id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(q)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`quote-delete-${q.id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:bg-red-500/10" onClick={() => remove(q.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {quotes.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-muted-foreground">No quote requests yet. They'll land here from your website's quote form.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!responding} onOpenChange={(o) => !o && setResponding(null)}>
        <DialogContent data-testid="quote-respond-dialog" aria-describedby={undefined} className="max-h-[85vh] overflow-y-auto">
          {responding && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Quote for {responding.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Button data-testid="quote-ai-draft-button" onClick={aiDraft} disabled={aiLoading} variant="outline"
                  className="w-full rounded-full border-[var(--cc-accent)]/50 text-[var(--cc-accent)] hover:bg-[var(--cc-accent)]/10 font-semibold">
                  {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting your quote...</> : <><Sparkles className="w-4 h-4 mr-2" /> {analysis ? "Re-run AI analysis" : "AI Draft — price + reply in one click"}</>}
                </Button>
                {analysis && (
                  <div data-testid="quote-ai-analysis" className="rounded-lg border border-[var(--cc-accent)]/30 bg-[var(--cc-accent)]/5 p-4 text-sm space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[var(--cc-accent)]" /> AI suggests {fmtMoney(analysis.suggested_price)}{analysis.photo_count ? ` · ${analysis.photo_count} photo(s) reviewed` : ""}</p>
                      <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs shrink-0" data-testid="quote-ai-apply"
                        onClick={() => { setPrice(String(analysis.suggested_price)); setMessage(analysis.message || ""); }}>Use this</Button>
                    </div>
                    {(analysis.breakdown || []).map((b, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="text-muted-foreground">{b.label}</span><span className="font-mono-cc">{fmtMoney(b.amount)}</span></div>
                    ))}
                    {analysis.reasoning && <p className="text-xs text-muted-foreground italic pt-2 border-t border-[var(--cc-accent)]/20">{analysis.reasoning}</p>}
                  </div>
                )}
                <div className="rounded-lg border bg-secondary/40 p-4 text-sm space-y-1">
                  <p><span className="text-muted-foreground">Service:</span> {serviceName(responding.service_type)}</p>
                  {responding.pickup_address && <p><span className="text-muted-foreground">Route:</span> {responding.pickup_address}{responding.dropoff_address ? ` → ${responding.dropoff_address}` : ""}</p>}
                  {responding.property_size && <p><span className="text-muted-foreground">Size:</span> {responding.property_size}</p>}
                  {(responding.stairs || responding.elevator) && <p><span className="text-muted-foreground">Access:</span> {responding.stairs ? `Stairs ×${responding.stairs_flights || 1}` : ""}{responding.stairs && responding.elevator ? " · " : ""}{responding.elevator ? "Elevator" : ""}</p>}
                  {responding.time_window && <p><span className="text-muted-foreground">Time:</span> {responding.time_window}</p>}
                  {(responding.items || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid="quote-detail-items">
                      {responding.items.map((it, i) => (
                        <span key={i} className="rounded-full border px-2 py-0.5 text-xs font-semibold bg-card">{it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}</span>
                      ))}
                    </div>
                  )}
                  {responding.internal_split && !isDispatcher && (
                    <p className="text-xs pt-1"><span className="text-muted-foreground">Your split:</span> You {fmtMoney(responding.internal_split.owner || 0)} · Helpers {fmtMoney(responding.internal_split.helper_pay || 0)} · Truck {fmtMoney(responding.internal_split.truck || 0)} · CastleCorp {fmtMoney(responding.internal_split.company || 0)}</p>
                  )}
                  {responding.details && <p className="text-muted-foreground italic">"{responding.details}"</p>}
                  {(detail?.photos || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2" data-testid="quote-detail-photos">
                      {detail.photos.map((p, i) => (
                        <a key={i} href={p} target="_blank" rel="noreferrer"><img src={p} alt="" className="w-16 h-16 object-cover rounded-md border" /></a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Flat-rate price ($)</Label>
                  <Input data-testid="quote-price-input" type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="1500" />
                </div>
                <div className="space-y-1.5">
                  <Label>Message to customer (optional)</Label>
                  <Textarea data-testid="quote-message-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Includes crew, truck, protection..." />
                </div>
                <Button data-testid="quote-respond-submit" onClick={respond} disabled={!price || Number(price) <= 0}
                  className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
                  Send Quote — Customer Gets Notified
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="quote-edit-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New Quote Request" : "Edit Quote"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="quote-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="quote-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Service</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger data-testid="quote-form-service"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Pickup address</Label><Input value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Dropoff address</Label><Input value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Property size</Label><Input value={form.property_size} onChange={(e) => setForm({ ...form, property_size: e.target.value })} placeholder="2BR" /></div>
              <div className="space-y-1.5"><Label>Preferred date</Label><Input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Details</Label><Textarea rows={2} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} /></div>
            <Button data-testid="quote-form-submit" onClick={saveForm} disabled={!form.name || !form.email}
              className="w-full rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
              {editing === "new" ? "Create Quote Request" : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
